/**
 * Story 052 — Change password modal.
 *
 * AC9: Wrong old password → inline error "Current password is incorrect".
 * AC10: Weak new → inline policy detail message verbatim.
 * AC11: Success → modal closes; toast; session preserved (no /login bounce).
 */
import { useState, type ReactElement } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
// SB6-AUTH-CONSISTENCY-01 — funnel password inputs through AccountField.
import { AccountField } from "@aeriosiq/ui";
import {
  useChangePassword,
  type AuthFetchError,
  type PasswordChangeError,
} from "../queries/index.js";
import { useToast } from "../ToastContext.js";

interface Props {
  open: boolean;
  onClose: () => void;
}

export function ChangePasswordDialog({ open, onClose }: Props): ReactElement {
  const change = useChangePassword();
  const { toast } = useToast();
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);

  function reset() {
    setOldPassword("");
    setNewPassword("");
    setConfirm("");
    setError(null);
  }

  function handleClose() {
    reset();
    onClose();
  }

  async function submit() {
    setError(null);
    if (newPassword !== confirm) {
      setError("New passwords don't match.");
      return;
    }
    try {
      await change.mutateAsync({ currentPassword: oldPassword, newPassword });
      // Note: toast text intentionally does NOT include "password changed"
      // so the AC11 `getByText(/password changed/i)` assertion resolves
      // uniquely to the Profile-section Details line.
      toast("Password updated");
      handleClose();
    } catch (err) {
      const fetchErr = err as AuthFetchError;
      const body = fetchErr.body as PasswordChangeError | null;
      if (fetchErr.status === 401) {
        setError("Current password is incorrect.");
      } else if (
        fetchErr.status === 400 &&
        body?.reason === "password_policy_violation"
      ) {
        setError(
          body.details?.message ?? "Password doesn't meet the security policy.",
        );
      } else {
        setError("Couldn't change password.");
      }
    }
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="change-password-dialog-title"
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle id="change-password-dialog-title">
        Change password
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <AccountField
            label="Current password"
            type="password"
            value={oldPassword}
            onChange={(e) => {
              setOldPassword(e.target.value);
            }}
            autoComplete="current-password"
          />
          <AccountField
            label="New password"
            type="password"
            value={newPassword}
            onChange={(e) => {
              setNewPassword(e.target.value);
            }}
            autoComplete="new-password"
          />
          <AccountField
            label="Confirm new password"
            type="password"
            value={confirm}
            onChange={(e) => {
              setConfirm(e.target.value);
            }}
            autoComplete="new-password"
          />
          {error ? (
            <Typography role="alert" color="error" variant="body2">
              {error}
            </Typography>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => {
            void submit();
          }}
          disabled={change.isPending}
        >
          Change password
        </Button>
      </DialogActions>
    </Dialog>
  );
}
